/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    FormControlLabel,
    Checkbox,
    InputLabel,
    Grid,
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
    Divider,
    Slide,
    AppBar,
    Toolbar,
    Chip,
    Paper,
    CardMedia,
} from '@mui/material';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    Close as CloseIcon,
    ShoppingCart as CartIcon,
} from '@mui/icons-material';
import './AddOrderDialog.css';

import {
    ordersAPI,
    categoryAPI,
    productAPI,
    tableAPI,
    customersAPI,
    settingsAPI,
} from '../../services/apiService';
/* eslint-disable prefer-arrow-callback, react/jsx-props-no-spreading */
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function AddOrderDialog({ open, onClose, onSuccess, onError }) {
    const { t, i18n } = useTranslation();
    const [categories, setCategories] = useState({});
    const [products, setProducts] = useState({});
    const [tables, setTables] = useState({});
    const [taxPercent, setTaxPercent] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedTable, setSelectedTable] = useState('');
    const [isTakeAway, setIsTakeAway] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('preparing');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [customerNameDialog, setCustomerNameDialog] = useState(false);
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [foundCustomer, setFoundCustomer] = useState(null);
    const [customerChecking, setCustomerChecking] = useState(false);
    const [customerCreating, setCustomerCreating] = useState(false);
    const [discountType, setDiscountType] = useState('amount');
    const [discountValue, setDiscountValue] = useState(0);
    const [isAnonymousCustomer, setIsAnonymousCustomer] = useState(false);

    const selectedItemsListRef = useRef(null);

    const toEnglishDigits = (value = '') =>
        value
            .toString()
            .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
            .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));

    const toPersianDigits = (value = '') => value.toString().replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

    const formatDigitsByLanguage = (value = '') =>
        i18n.language === 'fa' ? toPersianDigits(value) : value;

    const resetCustomerFlow = () => {
        setCustomerNameDialog(false);
        setCustomerPhone('');
        setCustomerName('');
        setFoundCustomer(null);
        setIsAnonymousCustomer(false);
        setCustomerChecking(false);
        setCustomerCreating(false);
        setDiscountType('amount');
        setDiscountValue(0);
    };

    const resetOrderForm = () => {
        setSelectedCategory('');
        setSelectedProduct('');
        setSelectedItems([]);
        setSelectedTable('');
        setIsTakeAway(false);
        setSelectedStatus('preparing');
        setNote('');
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [categoriesData, productsData, tablesData, settingsData] = await Promise.all([
                categoryAPI.getAll(),
                productAPI.getAll(),
                tableAPI.getAll(),
                settingsAPI.getSettings(),
            ]);

            setCategories(categoriesData || {});
            setProducts(productsData || {});
            setTables(tablesData || {});
            setTaxPercent(Number(settingsData?.tax || 0));
        } catch (error) {
            console.error('Error loading add order dialog data:', error);
            onError?.(
                error?.response?.data?.message || error?.message || t('failed_to_create_order')
            );
        } finally {
            setLoading(false);
        }
    }, [t, onError]);
    useEffect(() => {
        if (Object.keys(products).length > 0) {
            Object.values(products).forEach((product) => {
                if (product.image) {
                    const img = new Image();
                    img.src = product.image;
                }
            });
        }
    }, [products]);

    useEffect(() => {
        if (open) {
            fetchData();
        }
    }, [open, fetchData]);

    useEffect(() => {
        if (selectedItemsListRef.current && selectedItems.length > 0) {
            selectedItemsListRef.current.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
    }, [selectedItems]);

    const handleProductChange = (e) => {
        const productId = e.target.value;
        setSelectedProduct(productId);

        if (!productId) return;

        const product = products[productId];
        if (!product) return;

        const existingItemIndex = selectedItems.findIndex((item) => item.item === product.id);

        if (existingItemIndex !== -1) {
            const updatedItem = {
                ...selectedItems[existingItemIndex],
                quantity: selectedItems[existingItemIndex].quantity + 1,
            };
            updatedItem.total_price = updatedItem.quantity * updatedItem.item_price;
            const remainingItems = selectedItems.filter((_, index) => index !== existingItemIndex);
            setSelectedItems([...remainingItems, updatedItem]);
        } else {
            const newItem = {
                item: product.id,
                item_id: product.item_id,
                item_title: product.title,
                item_price: Number(product.price || 0),
                item_type: product.item_type,
                quantity: 1,
                total_price: Number(product.price || 0),
            };
            setSelectedItems([...selectedItems, newItem]);
        }

        setSelectedProduct('');
    };

    const handleRemoveItem = (index) => {
        setSelectedItems(selectedItems.filter((_, i) => i !== index));
    };

    const handleQuantityChange = (index, delta) => {
        const updatedItems = [...selectedItems];
        const newQuantity = updatedItems[index].quantity + delta;

        if (newQuantity <= 0) {
            handleRemoveItem(index);
            return;
        }

        updatedItems[index].quantity = newQuantity;
        updatedItems[index].total_price = newQuantity * updatedItems[index].item_price;
        setSelectedItems(updatedItems);
    };

    const handleClose = () => {
        resetOrderForm();
        resetCustomerFlow();
        onClose();
    };

    const handleSubmit = async () => {
        if (selectedItems.length === 0) {
            alert(t('select_item_and_table'));
            return;
        }
        if (!isTakeAway && !selectedTable) {
            alert(t('select_table_or_take_away'));
        }
    };
    const handleAnonymousChange = (event) => {
        const { checked } = event.target;

        setIsAnonymousCustomer(checked);

        if (checked) {
            setFoundCustomer(null);
            setCustomerName('');
            setCustomerPhone('');

            setDiscountType('amount');
            setDiscountValue(0);
        }
    };
    const handleCheckCustomer = async () => {
        const phone = toEnglishDigits(customerPhone).replace(/\D/g, '').slice(0, 11);

        if (phone.length !== 11) {
            alert(t('phone_number_must_be_11_digits'));
            return;
        }

        setCustomerChecking(true);

        try {
            const response = await customersAPI.getById(phone);
            const customerData = response?.data || response;

            if (customerData?.error === 'Customer not found') {
                setFoundCustomer(null);
                setCustomerName('');
                setCustomerPhone(phone);
                setCustomerNameDialog(true);
                return;
            }

            setFoundCustomer(customerData);
            setIsAnonymousCustomer(false);
            setCustomerName(customerData?.full_name || '');
            setCustomerPhone(
                toEnglishDigits(customerData?.phone_number || customerData?.uid || phone)
            );

            setDiscountType('amount');
            setDiscountValue(0);
        } catch (error) {
            console.error('Error checking customer:', error);

            onError?.(
                error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    error?.message ||
                    t('failed_to_check_customer')
            );
        } finally {
            setCustomerChecking(false);
        }
    };

    const handleCreateCustomer = async () => {
        const phone = toEnglishDigits(customerPhone).replace(/\D/g, '').slice(0, 11);
        const fullName = customerName.trim();

        if (!fullName) {
            alert(t('enter_customer_name'));
            return;
        }

        if (phone.length !== 11) {
            alert(t('phone_number_must_be_11_digits'));
            return;
        }

        setCustomerCreating(true);

        try {
            const payload = {
                full_name: fullName,
                phone_number: phone,
                credit: 0,
            };

            const result = await customersAPI.create(payload);
            const createdCustomer = result?.data || result || payload;

            setFoundCustomer(createdCustomer);
            setIsAnonymousCustomer(false);
            setCustomerPhone(toEnglishDigits(createdCustomer?.phone_number || phone));
            setCustomerName(createdCustomer?.full_name || fullName);
            setDiscountType('amount');
            setDiscountValue(0);
            setCustomerNameDialog(false);
        } catch (error) {
            console.error('Error creating customer:', error);

            onError?.(
                error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    error?.message ||
                    t('failed_to_create_customer')
            );
        } finally {
            setCustomerCreating(false);
        }
    };

    const getOrderItemsTotal = () =>
        selectedItems.reduce((sum, item) => sum + Number(item.total_price || 0), 0);

    const getDiscountAmount = () => {
        const total = getOrderItemsTotal();
        const value = Number(toEnglishDigits(discountValue)) || 0;
        if (discountType === 'amount') {
            return Math.min(value, total);
        }
        if (discountType === 'percent') {
            const percentAmount = (total * value) / 100;
            return Math.min(percentAmount, total);
        }
        if (discountType === 'credit') {
            const maxCredit = Number(foundCustomer?.credit || 0);
            return Math.min(value, total, maxCredit);
        }
        return 0;
    };

    const getFinalTotal = () => {
        const total = getOrderItemsTotal();
        const discount = getDiscountAmount();
        return total - discount;
    };

    const getTaxAmount = () => {
        const subtotalAfterDiscount = getFinalTotal();
        return (subtotalAfterDiscount * taxPercent) / 100;
    };

    const getGrandTotal = getFinalTotal() + getTaxAmount();

    const handleCreateOrder = async () => {
        if (selectedItems.length === 0) {
            alert(t('select_at_least_one_item'));
            return;
        }
        if (!isTakeAway && !selectedTable) {
            alert(t('select_table_or_take_away'));
            return;
        }
        if (!foundCustomer && !isAnonymousCustomer) {
            alert(t('customer_not_found'));
            return;
        }
        const orderData = {
            table_id: isTakeAway ? null : tables[selectedTable]?.table_id || selectedTable,
            status: selectedStatus,
            note,
            discount: getDiscountAmount() || 0,
            customer: isAnonymousCustomer ? '' : foundCustomer.id,
            items: selectedItems.map((item) => ({
                item: item.item,
                item_id: item.item_id,
                item_title: item.item_title,
                item_price: item.item_price,
                quantity: item.quantity,
                total_price: item.total_price,
            })),
        };
        console.log('FOUND CUSTOMER:', foundCustomer);
        console.log('IS ANONYMOUS CUSTOMER:', isAnonymousCustomer);
        console.log('ORDER DATA:', orderData);
        setSubmitting(true);
        try {
            await ordersAPI.create(orderData);
            onSuccess?.(t('order_created_successfully'));
            handleClose();
        } catch (error) {
            console.error('Error creating order:', error);
            console.error('SERVER RESPONSE:', error?.response?.data);
            onError?.(
                error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    JSON.stringify(error?.response?.data) ||
                    error?.message ||
                    t('failed_to_create_order')
            );
        } finally {
            setSubmitting(false);
        }
    };
    const getDiscountInputLabel = () => {
        if (discountType === 'amount') return t('enter_amount');
        if (discountType === 'percent') return t('enter_percent');
        return t('enter_credit_amount');
    };

    const filteredProducts = Object.entries(products).filter(([, product]) => {
        const isAvailable = product.available === true;
        const matchesCategory =
            !selectedCategory || Number(product.item_type) === Number(selectedCategory);

        return isAvailable && matchesCategory;
    });

    return (
        <>
            <Dialog
                fullScreen
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
                className="pos-dialog"
            >
                <AppBar sx={{ position: 'relative', bgcolor: '#5e806d' }}>
                    <Toolbar>
                        <IconButton edge="start" onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>

                <Box className="pos-dialog-body">
                    <Grid container spacing={2} className="pos-layout-grid">
                        <Grid item xs={12} md={8} className="pos-products-panel">
                            <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }} className="pos-full-body">
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        {/* Table Select */}
                                        <FormControl fullWidth disabled={isTakeAway}>
                                            <InputLabel>{t('tables')}</InputLabel>
                                            <Select
                                                value={selectedTable}
                                                label={t('tables')}
                                                onChange={(e) => setSelectedTable(e.target.value)}
                                            >
                                                {Object.entries(tables).map(([id, table]) => (
                                                    <MenuItem key={id} value={id}>
                                                        {table.table_id}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <FormControlLabel
                                            sx={{ mt: 1 }}
                                            control={
                                                <Checkbox
                                                    checked={isTakeAway}
                                                    onChange={(e) => {
                                                        const { checked } = e.target;
                                                        setIsTakeAway(checked);
                                                        if (checked) {
                                                            setSelectedTable('');
                                                        }
                                                    }}
                                                />
                                            }
                                            label={t('take_away') || 'Take away'}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <Select
                                                value={selectedStatus}
                                                onChange={(e) => setSelectedStatus(e.target.value)}
                                            >
                                                <MenuItem value="pending">
                                                    {t('order_status.pending')}
                                                </MenuItem>
                                                <MenuItem value="preparing">
                                                    {t('order_status.preparing')}
                                                </MenuItem>
                                                <MenuItem value="served">
                                                    {t('order_status.served')}
                                                </MenuItem>
                                                <MenuItem value="paid">
                                                    {t('order_status.paid')}
                                                </MenuItem>
                                                <MenuItem value="cancelled">
                                                    {t('order_status.cancelled')}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Paper>
                            <Box className="pos-category-chips">
                                <Chip
                                    label={t('all')}
                                    onClick={() => setSelectedCategory('')}
                                    color={selectedCategory === '' ? 'primary' : 'default'}
                                />
                                {Object.entries(categories).map(([key, cat]) => (
                                    <Chip
                                        key={key}
                                        label={cat.title}
                                        onClick={() => setSelectedCategory(cat.id)}
                                        color={selectedCategory === cat.id ? 'primary' : 'default'}
                                    />
                                ))}
                            </Box>
                            <Box className="pos-products-scroll">
                                <Grid container spacing={1.5}>
                                    {filteredProducts.map(([id, product]) => (
                                        <Grid item xs={12} sm={6} md={3} key={product.id || id}>
                                            <Card
                                                className="pos-product-card"
                                                onClick={() =>
                                                    handleProductChange({
                                                        target: { value: id },
                                                    })
                                                }
                                                sx={{
                                                    height: 210,
                                                    minWidth: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    cursor: 'pointer',
                                                    overflow: 'hidden',
                                                    borderRadius: 2,
                                                    '&:hover': {
                                                        boxShadow: 4,
                                                    },
                                                }}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    image={product.image}
                                                    alt={product.title}
                                                    sx={{
                                                        width: '100%',
                                                        height: 125,
                                                        objectFit: 'cover',
                                                        backgroundColor: '#e9ecef',
                                                    }}
                                                />
                                                <CardContent
                                                    sx={{
                                                        minWidth: 0,
                                                        flex: 1,
                                                        padding: 1,
                                                        '&:last-child': {
                                                            paddingBottom: 1,
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={800}
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {product.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            overflow: 'hidden',
                                                            WebkitBoxOrient: 'vertical',
                                                            WebkitLineClamp: 2,
                                                            marginTop: 0.5,
                                                        }}
                                                    >
                                                        {product.description}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="primary"
                                                        fontWeight={700}
                                                    >
                                                        {formatDigitsByLanguage(
                                                            Number(
                                                                product.price || 0
                                                            ).toLocaleString()
                                                        )}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4} className="pos-summary-panel">
                            <Paper className="summary-container">
                                <Box className="checkout-customer-section">
                                    {foundCustomer && !isAnonymousCustomer ? (
                                        <Box className="customer-found-box">
                                            <Box>
                                                <Typography
                                                    className="customer-found-name"
                                                    fontWeight={700}
                                                >
                                                    {foundCustomer.full_name || customerName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatDigitsByLanguage(
                                                        foundCustomer.phone_number || customerPhone
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setFoundCustomer(null);
                                                    setCustomerName('');
                                                    setCustomerPhone('');
                                                    setDiscountType('amount');
                                                    setDiscountValue(0);
                                                }}
                                            >
                                                {t('change')}
                                            </Button>
                                        </Box>
                                    ) : (
                                        <>
                                            <Box className="customer-input-row">
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="text"
                                                    inputMode="numeric"
                                                    label={t('phone_number')}
                                                    value={formatDigitsByLanguage(customerPhone)}
                                                    disabled={
                                                        isAnonymousCustomer || customerChecking
                                                    }
                                                    onChange={(e) => {
                                                        const englishValue = toEnglishDigits(
                                                            e.target.value
                                                        )
                                                            .replace(/\D/g, '')
                                                            .slice(0, 11);
                                                        setCustomerPhone(englishValue);
                                                    }}
                                                    placeholder={
                                                        i18n.language === 'fa'
                                                            ? '۰۹۱۲۳۴۵۶۷۸۹'
                                                            : '09123456789'
                                                    }
                                                />

                                                <Button
                                                    variant="contained"
                                                    onClick={handleCheckCustomer}
                                                    disabled={
                                                        isAnonymousCustomer ||
                                                        customerChecking ||
                                                        customerPhone.length !== 11
                                                    }
                                                    className="customer-search-btn"
                                                >
                                                    {customerChecking
                                                        ? t('loading')
                                                        : t('continue')}
                                                </Button>
                                            </Box>
                                            <FormControlLabel
                                                className="anonymous-checkbox"
                                                control={
                                                    <Checkbox
                                                        checked={isAnonymousCustomer}
                                                        onChange={handleAnonymousChange}
                                                    />
                                                }
                                                label={
                                                    t('anonymous_customer') || 'Anonymous customer'
                                                }
                                            />
                                        </>
                                    )}
                                    {foundCustomer && !isAnonymousCustomer && (
                                        <Box className="customer-credit-box">
                                            <Typography variant="body2">{t('credit')}</Typography>
                                            <Typography variant="body2" fontWeight={700}>
                                                {formatDigitsByLanguage(
                                                    Number(
                                                        foundCustomer?.credit || 0
                                                    ).toLocaleString()
                                                )}{' '}
                                                {t('toman')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                <Divider />
                                <Box className="summary-list" ref={selectedItemsListRef}>
                                    {selectedItems.length === 0 ? (
                                        <Box className="empty-order-message">
                                            <CartIcon />
                                            <Typography variant="body2">
                                                {t('select_item_and_table')}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        [...selectedItems].reverse().map((item, reversedIndex) => {
                                            const index = selectedItems.length - 1 - reversedIndex;
                                            return (
                                                <Box key={index} className="summary-item">
                                                    <Box className="summary-item-info">
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={700}
                                                        >
                                                            {item.item_title}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {formatDigitsByLanguage(
                                                                Number(
                                                                    item.item_price
                                                                ).toLocaleString()
                                                            )}{' '}
                                                            {t('toman')}
                                                        </Typography>
                                                    </Box>
                                                    <Box className="summary-item-controls">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleQuantityChange(index, -1)
                                                            }
                                                        >
                                                            <RemoveIcon />
                                                        </IconButton>
                                                        <Typography className="summary-quantity">
                                                            {formatDigitsByLanguage(item.quantity)}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleQuantityChange(index, 1)
                                                            }
                                                        >
                                                            <AddIcon />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            );
                                        })
                                    )}
                                </Box>
                                <Box className="summary-footer">
                                    <Box className="checkout-discount-section">
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                            className="checkout-section-title"
                                        >
                                            {t('discount')}
                                        </Typography>
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            className="discount-type-select"
                                        >
                                            <InputLabel>{t('discount_type')}</InputLabel>
                                            <Select
                                                value={discountType}
                                                label={t('discount_type')}
                                                onChange={(e) => {
                                                    setDiscountType(e.target.value);
                                                    setDiscountValue(0);
                                                }}
                                            >
                                                <MenuItem value="amount">
                                                    {t('discount_amount')}
                                                </MenuItem>
                                                <MenuItem value="percent">
                                                    {t('discount_percent')}
                                                </MenuItem>
                                                <MenuItem
                                                    value="credit"
                                                    disabled={isAnonymousCustomer || !foundCustomer}
                                                >
                                                    {t('discount_credit')} (
                                                    {formatDigitsByLanguage(
                                                        foundCustomer?.credit || 0
                                                    )}{' '}
                                                    {t('toman')})
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="text"
                                            inputMode="numeric"
                                            label={getDiscountInputLabel()}
                                            value={formatDigitsByLanguage(discountValue)}
                                            disabled={selectedItems.length === 0}
                                            onChange={(e) => {
                                                const englishValue = toEnglishDigits(
                                                    e.target.value
                                                ).replace(/\D/g, '');
                                                setDiscountValue(englishValue);
                                            }}
                                        />
                                    </Box>
                                    <Divider />
                                    <Box className="price-summary-section">
                                        <Box className="price-summary-row">
                                            <Typography>{t('total')}</Typography>
                                            <Typography fontWeight={600}>
                                                {formatDigitsByLanguage(
                                                    getOrderItemsTotal().toLocaleString()
                                                )}{' '}
                                                {t('toman')}
                                            </Typography>
                                        </Box>
                                        <Box className="price-summary-row">
                                            <Typography>{t('discount')}</Typography>
                                            <Typography fontWeight={600} className="discount-price">
                                                -
                                                {formatDigitsByLanguage(
                                                    getDiscountAmount().toLocaleString()
                                                )}{' '}
                                                {t('toman')}
                                            </Typography>
                                        </Box>
                                        <Box className="price-summary-row">
                                            <Typography>{t('tax')}</Typography>
                                            <Typography fontWeight={600}>
                                                {formatDigitsByLanguage(
                                                    getTaxAmount().toLocaleString()
                                                )}{' '}
                                                {t('toman')}
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Box className="price-summary-row final-row">
                                            <Typography variant="h6" fontWeight={800}>
                                                {t('final_total')}
                                            </Typography>
                                            <Typography variant="h6" fontWeight={800}>
                                                {formatDigitsByLanguage(
                                                    getGrandTotal.toLocaleString()
                                                )}{' '}
                                                {t('toman')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        className="confirm-order-btn"
                                        onClick={handleCreateOrder}
                                        disabled={
                                            submitting ||
                                            selectedItems.length === 0 ||
                                            (!isAnonymousCustomer && !foundCustomer) ||
                                            (!isTakeAway && !selectedTable)
                                        }
                                    >
                                        {submitting ? t('submitting') : t('confirm_order')}
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Dialog>
            <Dialog
                open={customerNameDialog}
                onClose={() => {
                    if (!customerCreating) {
                        setCustomerNameDialog(false);
                    }
                }}
                maxWidth="xs"
                fullWidth
                className="customer-create-dialog"
            >
                <DialogTitle>{t('enter_customer_name')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {formatDigitsByLanguage(customerPhone)}
                    </Typography>

                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        type="text"
                        label={t('full_name')}
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCustomerNameDialog(false)}
                        disabled={customerCreating}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleCreateCustomer}
                        variant="contained"
                        disabled={customerCreating || !customerName.trim()}
                    >
                        {customerCreating ? t('loading') : t('continue')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default AddOrderDialog;
